import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Header from "../../components/Header";
import PageBanner from "../../components/PageBanner";
import Footer from "../../components/Footer";
import "../home/homeRedesign.css";
import "./departmentsNew.css";
import { getGlobalDepartments, getDepartments } from '../../services/data.service';
import { mediaUrl, assetUrl } from '../../services/assetUrl';

const EASE = [0.23, 1, 0.32, 1];

/* ------------------------------------------------------------------ icons */
const IconSchool = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 9l-10-4-10 4 10 4 10-4v6" />
    <path d="M6 10.6v5.4a6 3 0 0012 0v-5.4" />
  </svg>
);

const IconCertificate = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="15" r="3" />
    <path d="M13 17.5v4.5l2-1.5 2 1.5v-4.5" />
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M7 9h10M7 12h3" />
  </svg>
);

const IconFlask = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 3h6M10 9h4M10 3v6L6 20a1 1 0 00.8 1.6h10.4a1 1 0 00.8-1.6l-4-11V3" />
  </svg>
);

const IconSearch = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3-3" />
  </svg>
);

const IconArrow = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const IconResearch = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 3h6v7l5 9H4l5-9V3z" />
    <path d="M9 3h6" />
  </svg>
);

const IconIndustry = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6" />
    <path d="M9 9h1M14 9h1M9 13h1M14 13h1" />
  </svg>
);

const IconInnovation = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 18h6M10 22h4M12 2a7 7 0 00-4 12.7V17h8v-2.3A7 7 0 0012 2z" />
  </svg>
);

// Backend stores only an icon key string -> resolve to an SVG component here.
const CATEGORY_ICONS = { school: IconSchool, certificate: IconCertificate, flask: IconFlask };
const PILLAR_ICONS = { research: IconResearch, industry: IconIndustry, innovation: IconInnovation };

/* ------------------------------------------------------------------ helpers */
// Render a heading with the last word highlighted in <span> (matches design).
const HighlightHeading = ({ text }) => {
  const parts = (text || "").trim().split(" ");
  if (parts.length <= 1) return <>{text}</>;
  const last = parts.pop();
  return <>{parts.join(" ")} <span>{last}</span></>;
};

// Build the page model purely from backend chrome + department list (DB-only).
const buildModel = (global, deptList) => {
  const categories = {};
  const tabKeys = [];

  (global?.browseByLevel?.categories || []).forEach((c) => {
    categories[c.id] = {
      title: c.title || c.label || "",
      short: c.label || "",
      tag: c.tag || "",
      icon: c.icon || "school",
      items: [],
    };
    tabKeys.push(c.id);
  });

  (deptList || []).forEach((d) => {
    if (!categories[d.category]) {
      categories[d.category] = { title: "", short: d.category, tag: "", icon: "school", items: [] };
      tabKeys.push(d.category);
    }
    categories[d.category].items.push({ title: d.name, id: d.slug, code: d.code || "" });
  });

  const hero = global?.heroSection || {};
  const why = global?.whyStudySection || {};
  const emp = global?.empoweringSection || {};

  return {
    hero: {
      eyebrow: hero.subheading || "",
      heading: hero.heading || "",
      description: hero.description || "",
      bannerImage: hero.bannerImage || "",
      stats: (hero.badges || []).map((b) => ({ value: b.value, label: b.label })),
    },
    tabKeys,
    categories,
    why: {
      heading: why.heading || "",
      description: why.description || "",
      pillars: (why.features || []).map((f) => ({ title: f.title, text: f.description, icon: f.icon })),
    },
    showcase: {
      heading: emp.heading || "",
      description: emp.description || "",
      backgroundImage: emp.backgroundImage || "",
      metrics: emp.stats || [],
    },
  };
};

/* ============================================================ component */
const Departments = () => {
  const { search } = useLocation();
  const tabParam = new URLSearchParams(search).get("tab");

  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(null);
  const [query, setQuery] = useState("");

  // Fetch chrome + department list from the backend (content is DB-only).
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    (async () => {
      try {
        const [globalRes, deptRes] = await Promise.all([
          getGlobalDepartments(),
          getDepartments(),
        ]);
        if (!mounted) return;
        const global = globalRes?.data || null;
        const deptList = deptRes?.data || [];
        setModel(global ? buildModel(global, deptList) : null);
      } catch (e) {
        if (mounted) setModel(null);
        console.warn("Departments: failed to load content", e?.message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Pick a valid active tab once the model is available (honours ?tab=).
  useEffect(() => {
    if (!model) return;
    setActiveTab((prev) => {
      if (tabParam && model.tabKeys.includes(tabParam)) return tabParam;
      if (prev && model.tabKeys.includes(prev)) return prev;
      return model.tabKeys[0] || null;
    });
  }, [tabParam, model]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setQuery("");
  }, [activeTab]);

  const category = (model && activeTab && model.categories[activeTab]) || { items: [], title: "", tag: "" };
  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return category.items;
    return category.items.filter(
      (item) =>
        item.title.toLowerCase().includes(q) ||
        (item.code || "").toLowerCase().includes(q)
    );
  }, [category.items, query]);

  // ---- loading / empty states (DB-only: no hardcoded fallback content) ----
  if (loading) {
    return (
      <div className="dptr-page">
        <Header parentMenu="study" topbarEnable="enable" />
        <PageBanner
          title="Loading Departments…"
          subtitle="Fetching the latest programme information."
          breadcrumbs={[{ label: "Home", path: "/" }, { label: "Departments" }]}
        />
        <Footer />
      </div>
    );
  }

  if (!model) {
    return (
      <div className="dptr-page">
        <Header parentMenu="study" topbarEnable="enable" />
        <PageBanner
          title="Departments Unavailable"
          subtitle="We couldn’t load the departments right now. Please try again later."
          breadcrumbs={[{ label: "Home", path: "/" }, { label: "Departments" }]}
        />
        <Footer />
      </div>
    );
  }

  return (
    <div className="dptr-page">
      <Header parentMenu="study" topbarEnable="enable" />

      {/* Shared PageBanner — Departments is the only page with breadcrumbs */}
      <PageBanner
        title={model.hero.heading}
        eyebrow={model.hero.eyebrow}
        subtitle={model.hero.description}
        image={model.hero.bannerImage ? mediaUrl(model.hero.bannerImage) : undefined}
        breadcrumbs={[{ label: "Home", path: "/" }, { label: "Departments" }]}
      />

      {/* Programme directory */}
      <section className="dptr-directory">
        <div className="container">
          <div className="dptr-directory__inner">
            {/* Category sidebar */}
            <aside className="dptr-cats" aria-label="Programme categories">
              <span className="dptr-cats__label">Browse by Level</span>
              <ul className="dptr-cats__list">
                {model.tabKeys.map((key) => {
                  const cat = model.categories[key];
                  if (!cat) return null;
                  const Icon = CATEGORY_ICONS[cat.icon] || IconSchool;
                  return (
                    <li key={key}>
                      <button
                        type="button"
                        className={`dptr-cats__btn${activeTab === key ? " is-active" : ""}`}
                        onClick={() => setActiveTab(key)}
                        aria-pressed={activeTab === key}
                      >
                        <span className="dptr-cats__icon"><Icon /></span>
                        <span className="dptr-cats__text">
                          <span className="dptr-cats__name">{cat.short}</span>
                          <span className="dptr-cats__count">{cat.items.length} programmes</span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>

            {/* Main panel */}
            <div className="dptr-panel">
              <div className="dptr-panel__head">
                <div className="dptr-panel__head-row">
                  <div>
                    <h2 className="dptr-panel__title">{category.title}</h2>
                    <p className="dptr-panel__meta">
                      Showing <strong>{filteredItems.length}</strong> of <strong>{category.items.length}</strong> programmes
                    </p>
                  </div>
                  <div className="dptr-search">
                    <span className="dptr-search__icon"><IconSearch /></span>
                    <input
                      type="search"
                      className="dptr-search__input"
                      placeholder="Search programmes…"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      aria-label="Search programmes"
                    />
                  </div>
                </div>
              </div>

              <AnimatePresence mode="wait">
                <motion.ul
                  key={`${activeTab}-${query}`}
                  className="dptr-list"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.3, ease: EASE }}
                >
                  {filteredItems.length > 0 ? (
                    filteredItems.map((item, idx) => (
                      <li key={item.id}>
                        <Link to={`/department/${item.id}`} className="dptr-row">
                          <span className="dptr-row__num">{String(idx + 1).padStart(2, "0")}</span>
                          <span className="dptr-row__body">
                            <span className="dptr-row__title">{item.title}</span>
                            <span className="dptr-row__tag">{item.code} · {category.tag}</span>
                          </span>
                          <span className="dptr-row__action">
                            Explore
                            <IconArrow />
                          </span>
                        </Link>
                      </li>
                    ))
                  ) : (
                    <li className="dptr-empty">
                      <strong>No programmes found</strong>
                      Try a different search term or switch category.
                    </li>
                  )}
                </motion.ul>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars + campus showcase */}
      <section className="dptr-pillars">
        <div className="container">
          <div className="dptr-pillars__intro">
            <h2>{model.why.heading}</h2>
            <p>{model.why.description}</p>
          </div>

          <div className="dptr-pillars__grid">
            {model.why.pillars.map(({ title, text, icon }) => {
              const Icon = PILLAR_ICONS[icon] || IconResearch;
              return (
                <article key={title} className="dptr-pillar">
                  <span className="dptr-pillar__icon"><Icon /></span>
                  <h3 className="dptr-pillar__title">{title}</h3>
                  <p className="dptr-pillar__text">{text}</p>
                </article>
              );
            })}
          </div>

          {/* Redesigned Empowering Students to Lead Showcase (Matching 2nd Image Reference) */}
          <div className="dptr-showcase-v2">
            <div className="dptr-showcase-v2__left">
              <h2 className="dptr-showcase-v2__heading">
                {model.showcase.heading || "Empowering Students to Lead"}
              </h2>
              <div className="dptr-showcase-v2__divider" />
              <p className="dptr-showcase-v2__text">
                {model.showcase.description ||
                  "Our curriculum is designed to challenge students and push them to reach their full potential. With state-of-the-art facilities and a dedicated faculty, we provide the environment needed for students to excel."}
              </p>
            </div>

            <div className="dptr-showcase-v2__gallery">
              {[
                { fallbackImg: "/images/challenge/challenge_1.png", fallbackTitle: "Graduation Celebration", cls: "dptr-showcase-v2__photo-wrap--1" },
                { fallbackImg: "/images/challenge/challenge_2.png", fallbackTitle: "Student Leadership & Speech", cls: "dptr-showcase-v2__photo-wrap--2" },
                { fallbackImg: "/images/challenge/challenge_3.png", fallbackTitle: "Campus Avenue & Greenery", cls: "dptr-showcase-v2__photo-wrap--3" },
                { fallbackImg: "/images/challenge/challenge_4.png", fallbackTitle: "Proud NCET Graduate", cls: "dptr-showcase-v2__photo-wrap--4" },
              ].map((item, idx) => {
                const dynamicItem = model.showcase.images?.[idx];
                const imgSrc = dynamicItem?.image ? mediaUrl(dynamicItem.image) : assetUrl(item.fallbackImg);
                const titleText = dynamicItem?.title || item.fallbackTitle;

                return (
                  <div key={idx} className={`dptr-showcase-v2__photo-wrap ${item.cls}`}>
                    <img
                      src={imgSrc}
                      alt={titleText}
                      className="dptr-showcase-v2__img"
                      loading="lazy"
                    />
                    <div className="dptr-showcase-v2__title-overlay">
                      <span className="dptr-showcase-v2__img-title">{titleText}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Departments;
